import { useState, useCallback } from 'react';
import { supabase } from '../supabase';

interface DownloadStats {
  total_downloads: number;
  downloads_today: number;
  downloads_this_week: number;
  downloads_this_month: number;
  unique_users: number;
}

export const useDownloadTracking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logDownload = useCallback(async (
    pluginId: string, 
    downloadType: string = 'web',
    userAgent?: string
  ) => {
    try {
      setError(null);
      
      // Simple fallback - just increment a counter without complex tracking
      const { data, error } = await supabase
        .from('plugin_downloads')
        .insert({
          plugin_id: pluginId,
          download_type: downloadType,
          user_agent: userAgent || navigator.userAgent,
          user_ip: null // Let the database handle this
        })
        .select()
        .single();

      if (error) {
        console.warn('Download tracking failed:', error);
        return null; // Don't throw, just log and continue
      }

      return data;
    } catch (err) {
      console.warn('Error logging download:', err);
      setError(null); // Don't show error to user
      return null;
    }
  }, []);

  const getDownloadStats = useCallback(async (pluginId: string): Promise<DownloadStats | null> => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc('get_plugin_download_stats', {
        plugin_id_param: pluginId
      });

      if (error) throw error;

      return data;
    } catch (err) {
      console.error('Error fetching download stats:', err);
      setError('Failed to fetch download stats');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getEnhancedPluginStats = useCallback(async (pluginId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Try enhanced stats first, fall back to original if it fails
      let { data, error } = await supabase.rpc('get_plugin_stats_enhanced', {
        plugin_id_param: pluginId
      });

      if (error) {
        console.warn('Enhanced stats failed, falling back to original:', error);
        // Fall back to original stats
        const result = await supabase.rpc('get_plugin_stats', {
          plugin_id_param: pluginId
        });
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      return data;
    } catch (err) {
      console.error('Error fetching plugin stats:', err);
      setError('Failed to fetch plugin stats');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    logDownload,
    getDownloadStats,
    getEnhancedPluginStats,
    loading,
    error
  };
}; 